import React, { useState, useEffect } from 'react';
import {
  Card,
  Upload,
  Button,
  Select,
  Form,
  message,
  Alert,
  Space,
  Typography,
  Progress,
  Input,
  Tabs,
  Divider,
} from 'antd';
import { UploadOutlined, CheckCircleOutlined, PhoneOutlined } from '@ant-design/icons';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import { adminService, employeeService } from '../../../services';
import { formatMexicoTime } from '../../../utils/timeUtils';
import type { Dataset, UploadTaskResponse, ManualEntryResponse } from '../../../types';

const { Title, Text } = Typography;

const AdminUploadPage: React.FC = () => {
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [selectedDataset, setSelectedDataset] = useState<number | undefined>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadResult, setUploadResult] = useState<UploadTaskResponse | null>(null);
  const [loading, setLoading] = useState(false);

  // 手动入库相关状态
  const [manualDatasetId, setManualDatasetId] = useState<number | undefined>();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [manualLoading, setManualLoading] = useState(false);
  const [manualResult, setManualResult] = useState<ManualEntryResponse | null>(null);

  useEffect(() => {
    const fetchDatasets = async () => {
      try {
        const response = await adminService.getDatasets();
        setDatasets(response.datasets);
      } catch (error) {
        console.error('获取数据集列表失败:', error);
      }
    };

    fetchDatasets();
  }, []);

  const handleUpload = async () => {
    if (!selectedDataset) {
      message.error('请选择数据集');
      return;
    }

    if (fileList.length === 0) {
      message.error('请选择文件');
      return;
    }

    try {
      setLoading(true);
      const uploadFile = fileList[0];
      console.log('uploadFile:', uploadFile);
      const file = uploadFile.originFileObj || uploadFile as any;
      console.log('file:', file);
      
      // 验证文件对象存在
      if (!file) {
        message.error('文件读取失败，请重新选择文件');
        return;
      }
      
      // 验证文件内容不为空
      if (file.size === 0) {
        message.error('文件不能为空');
        return;
      }
      
      const response = await employeeService.uploadFileTask(selectedDataset, file);
      setUploadResult(response);
      message.success(`文件 ${response.filename} 上传成功，正在后台处理中`);
      
      // 3秒后跳转到任务列表页面
      setTimeout(() => {
        window.location.href = '/admin/my-uploads';
      }, 3000);
    } catch (error: any) {
      console.error('文件上传失败:', error);
      // 显示具体的错误信息
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error.message) {
        message.error(error.message);
      } else {
        message.error('文件上传失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setUploadResult(null);
    setFileList([]);
    setSelectedDataset(undefined);
  };

  // 手动入库功能
  const handleManualEntry = async () => {
    if (!manualDatasetId) {
      message.error('请选择数据集');
      return;
    }

    if (!phoneNumber.trim()) {
      message.error('请输入电话号码');
      return;
    }

    // 输入框已限制只能输入数字，无需额外验证格式

    try {
      setManualLoading(true);
      const response = await adminService.createManualEntry({
        dataset_id: manualDatasetId,
        phone_number: phoneNumber.trim(),
      });
      setManualResult(response);
      // message.success('手动入库成功');
      setPhoneNumber(''); // 清空输入框，方便继续输入下一个
    } catch (error: any) {
      console.error('手动入库失败:', error);
    } finally {
      setManualLoading(false);
    }
  };

  const handleManualReset = () => {
    setManualResult(null);
    setPhoneNumber('');
    // 保持数据集选择，不重置 manualDatasetId
  };

  const uploadProps = {
    fileList,
    beforeUpload: (file: RcFile) => {
      // 检查文件扩展名
      if (!file.name.toLowerCase().endsWith('.txt')) {
        message.error('只允许上传.txt格式的文件');
        return false;
      }
      
      // 检查文件大小 (限制为10MB)
      if (file.size > 10 * 1024 * 1024) {
        message.error('文件大小不能超过10MB');
        return false;
      }
      
      // 创建包含originFileObj的文件对象
      const fileObj: UploadFile = {
        uid: file.name + Date.now(),
        name: file.name,
        status: 'done',
        originFileObj: file,
      };
      setFileList([fileObj]);
      return false; // 阻止自动上传
    },
    onRemove: () => {
      setFileList([]);
    },
    accept: '.txt',
    maxCount: 1,
  };

  return (
    <div>
      <Title level={2}>管理员数据录入</Title>
      
      <Tabs defaultActiveKey="manual" size="large" items={[
        {
          key: 'manual',
          label: '手动入库',
          children: (
            <>
              <Card title="手动入库单个电话号码" style={{ marginBottom: 24 }}>
                <Form layout="vertical">
                  <Form.Item label="选择数据集" required>
                    <Select
                      placeholder="请选择数据集"
                      value={manualDatasetId}
                      onChange={setManualDatasetId}
                    >
                      {datasets.map(dataset => (
                        <Select.Option key={dataset.id} value={dataset.id}>
                          {dataset.name} ({dataset.phone_count} 条记录)
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item label="电话号码" required>
                    <Input
                      placeholder="请输入手机号码"
                      value={phoneNumber}
                      onChange={(e) => {
                        // 只允许输入数字
                        const value = e.target.value.replace(/[^\d]/g, '');
                        setPhoneNumber(value);
                      }}
                      prefix={<PhoneOutlined />}
                    />
                    <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                      请输入手机号码（仅限数字）
                    </Text>
                  </Form.Item>

                  <Form.Item>
                    <Space>
                      <Button
                        type="primary"
                        onClick={handleManualEntry}
                        loading={manualLoading}
                      >
                        立即入库
                      </Button>
                      {manualResult && (
                        <Button onClick={handleManualReset}>
                          清除结果
                        </Button>
                      )}
                    </Space>
                  </Form.Item>
                </Form>
              </Card>

              {manualResult && (
                <Card title="入库成功">
                  <Alert
                    message="手动入库成功"
                    description={
                      <div>
                        <p>电话号码 <strong>{manualResult.entry.phone_number}</strong> 已成功添加到数据集 <strong>{manualResult.entry.dataset_name}</strong></p>
                        <p>入库ID: {manualResult.entry.id}</p>
                        <p>操作员: {manualResult.entry.operator}</p>
                        <p>入库时间: {formatMexicoTime(manualResult.entry.entered_at)}</p>
                        <Divider />
                        <p style={{ color: '#666' }}>
                          <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                          数据集已保持选择状态，您可以直接输入下一个电话号码继续入库。
                        </p>
                      </div>
                    }
                    type="success"
                    style={{ marginBottom: 16 }}
                  />
                </Card>
              )}
            </>
          ),
        },
        {
          key: 'file',
          label: '批量上传',
          children: (
            <>
              <Card title="上传电话号码文件" style={{ marginBottom: 24 }}>
                <Form layout="vertical">
                  <Form.Item label="选择数据集" required>
                    <Select
                      placeholder="请选择数据集"
                      value={selectedDataset}
                      onChange={setSelectedDataset}
                      disabled={!!uploadResult}
                    >
                      {datasets.map(dataset => (
                        <Select.Option key={dataset.id} value={dataset.id}>
                          {dataset.name} ({dataset.phone_count} 条记录)
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item label="选择文件" required>
                    <Upload {...uploadProps} maxCount={1}>
                      <Button icon={<UploadOutlined />} disabled={!!uploadResult}>
                        选择文件
                      </Button>
                    </Upload>
                    <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                      仅支持.txt格式文件，UTF-8编码，每行一个手机号码（仅限数字）
                    </Text>
                  </Form.Item>

                  <Form.Item>
                    <Space>
                      <Button
                        type="primary"
                        onClick={handleUpload}
                        loading={loading}
                        disabled={!!uploadResult}
                      >
                        上传文件
                      </Button>
                      {uploadResult && (
                        <Button onClick={handleReset}>
                          重新上传
                        </Button>
                      )}
                    </Space>
                  </Form.Item>
                </Form>
              </Card>

              {uploadResult && (
                <Card title="上传成功">
                  <Alert
                    message="文件上传成功"
                    description={
                      <div>
                        <p>文件 <strong>{uploadResult.filename}</strong> 已成功上传到数据集 <strong>{uploadResult.dataset.name}</strong></p>
                        <p>任务ID: {uploadResult.task_id}</p>
                        <p>系统正在后台处理您的文件，您可以在"我的上传"页面查看处理进度。</p>
                        <Progress percent={100} status="success" />
                        <p style={{ marginTop: 16, color: '#666' }}>
                          <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                          3秒后将自动跳转到任务列表页面...
                        </p>
                      </div>
                    }
                    type="success"
                    style={{ marginBottom: 16 }}
                  />
                </Card>
              )}
            </>
          ),
        },
      ]} />
    </div>
  );
};

export default AdminUploadPage;
